import React, { useState, useEffect } from 'react';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../config/firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faGlobe, faMagic } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

const PAGES = [
  { id: 'home',             name: 'Home',            path: '/' },
  { id: 'dharampur-villa',  name: 'Dharampur Villa',  path: '/dharampur-villa' },
  { id: 'barog-villa',      name: 'Barog Villa',      path: '/barog-villa' },
  { id: 'about',            name: 'About Us',         path: '/about' },
  { id: 'gallery',          name: 'Gallery',          path: '/gallery' },
  { id: 'blogs',            name: 'Blogs',            path: '/blogs' },
  { id: 'testimonials',     name: 'Testimonials',     path: '/testimonials' },
  { id: 'faqs',             name: 'FAQs',             path: '/faqs' },
  { id: 'contact',          name: 'Contact Us',       path: '/contact' },
  { id: 'flavors',          name: 'Flavors',          path: '/flavors' },
  { id: 'hospitality',      name: 'Hospitality',      path: '/hospitality' },
  { id: 'sustainability',   name: 'Sustainability',   path: '/sustainability' },
];

const DEFAULTS = {
  home: {
    title: 'The Vivaak | Luxury Villas in Dharampur & Barog, Himachal Pradesh',
    description: 'Book a luxurious private villa stay at The Vivaak in Dharampur and Twilight in Barog. Stunning mountain views, private pool, jacuzzi, and premium amenities in Himachal Pradesh.',
    keywords: 'luxury villa dharampur, private villa himachal pradesh, the vivaak villa, dharampur villa stay, boutique villa india, pine hills villa',
  },
  about: {
    title: 'About The Vivaak | Our Story, Vision & Luxury Hospitality',
    description: 'Discover the story behind The Vivaak — a family-run luxury villa experience nestled in the pine hills of Himachal Pradesh. Learn about our values and commitment to world-class hospitality.',
    keywords: 'about the vivaak, vivaak story, luxury villa india, pine hills villa, himachal villa experience, family villa',
  },
  gallery: {
    title: 'Gallery | The Vivaak — Luxury Villa Photos & Videos',
    description: 'Explore stunning photos and videos of The Vivaak villas — infinity pool, bedrooms, outdoor spaces, interiors, and breathtaking Himalayan views from Dharampur and Barog.',
    keywords: 'vivaak villa gallery, dharampur villa photos, barog villa photos, luxury villa images, himalayan villa, pool villa photos',
  },
  blogs: {
    title: 'Travel Blogs | The Vivaak — Himachal Pradesh Stays & Tips',
    description: 'Read travel guides, stay tips, and stories about Dharampur and Barog from The Vivaak blog. Plan your perfect Himachal Pradesh mountain getaway.',
    keywords: 'himachal travel blog, dharampur travel guide, barog getaway, vivaak blog, pine hills travel, himachal holiday tips',
  },
  testimonials: {
    title: "Guest Reviews | The Vivaak — Rated India's Favourite Homestay",
    description: "Read what our guests say about their stay at The Vivaak. Rated India's Favourite Homestay 2023 — luxury villa stays loved by travellers across India.",
    keywords: 'vivaak reviews, luxury villa reviews, dharampur villa testimonials, india favourite homestay, guest reviews villa',
  },
  faqs: {
    title: 'FAQs | The Vivaak — Booking, Stay & Amenities Questions Answered',
    description: 'Got questions? Find answers about bookings, check-in, amenities, cancellation policies, and everything you need to know before your stay at The Vivaak luxury villas.',
    keywords: 'vivaak faqs, villa booking questions, dharampur villa faq, barog villa questions, luxury villa stay info, villa policies',
  },
  contact: {
    title: 'Contact Us | The Vivaak — Book Your Stay or Get in Touch',
    description: 'Contact The Vivaak team for bookings, enquiries, or any assistance. We are happy to help you plan your perfect luxury villa stay in Dharampur or Barog, Himachal Pradesh.',
    keywords: 'contact vivaak, vivaak villa contact, dharampur villa booking, barog villa enquiry, luxury villa contact, villa reservation',
  },
  flavors: {
    title: 'Flavors of Vivaak | Curated Culinary Experiences in the Hills',
    description: 'Savour handcrafted culinary experiences at The Vivaak — from local Himachali cuisine to gourmet spreads. Taste the authentic flavours of the mountains at our luxury villas.',
    keywords: 'vivaak flavors, himachali cuisine, luxury villa dining, dharampur food experience, mountain cuisine, gourmet villa dining',
  },
  hospitality: {
    title: 'Vivaak Hospitality | Premium Villa Stay Services & Experiences',
    description: 'Experience world-class hospitality at The Vivaak. From personalised butler service to premium amenities — every detail is crafted to make your luxury villa stay unforgettable.',
    keywords: 'vivaak hospitality, premium villa services, luxury villa experience, dharampur hospitality, himachal villa service, villa amenities',
  },
  sustainability: {
    title: 'Sustainability | The Vivaak — Eco-Friendly Luxury in the Hills',
    description: 'The Vivaak is committed to sustainable and responsible tourism in Himachal Pradesh. Discover our green initiatives and eco-friendly practices at our luxury mountain villas.',
    keywords: 'vivaak sustainability, eco friendly villa india, responsible tourism himachal, green villa dharampur, sustainable luxury stay, eco villa',
  },
  'dharampur-villa': {
    title: 'The Vivaak, Dharampur | Private Luxury Villa with Infinity Pool',
    description: 'Stay at The Vivaak in Dharampur — a stunning private luxury villa with an infinity pool, panoramic Himalayan views, premium bedrooms, and curated mountain experiences.',
    keywords: 'dharampur villa, vivaak dharampur, luxury villa dharampur, private pool villa himachal, dharampur holiday villa, infinity pool villa',
  },
  'barog-villa': {
    title: 'Twilight, Barog | Luxury Mountain Villa with Jacuzzi & Views',
    description: 'Experience Twilight at Barog — a premium mountain villa with private jacuzzi, stunning contemporary interiors, and breathtaking Himalayan views. Perfect for a romantic getaway.',
    keywords: 'barog villa, twilight barog, luxury villa barog, barog jacuzzi villa, himachal villa stay, romantic villa barog, mountain villa',
  },
};

const SeoManager = () => {
  const [seoData, setSeoData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [activeTab, setActiveTab] = useState('home');

  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        const snap = await get(ref(database, 'seo_metadata'));
        setSeoData(snap.exists() ? snap.val() : {});
      } catch (error) {
        console.error('Error fetching SEO data:', error);
        toast.error('Failed to load SEO data');
      } finally {
        setLoading(false);
      }
    };
    fetchSeoData();
  }, []);

  const handleInputChange = (pageId, field, value) => {
    setSeoData(prev => ({
      ...prev,
      [pageId]: { ...(prev[pageId] || {}), [field]: value },
    }));
  };

  const handleSave = async (pageId) => {
    setSaving(true);
    try {
      const pageData = seoData[pageId] || {};
      const payload = { ...pageData, updatedAt: new Date().toISOString() };
      await set(ref(database, `seo_metadata/${pageId}`), payload);
      setSeoData(prev => ({ ...prev, [pageId]: payload }));
      toast.success(`SEO saved for ${pageId}`);
    } catch (error) {
      console.error('Error saving SEO data:', error);
      toast.error('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Writes default data for every page that is currently empty
  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const updates = {};
      const now = new Date().toISOString();
      PAGES.forEach(({ id }) => {
        if (!seoData[id]?.title) {
          updates[id] = { ...DEFAULTS[id], updatedAt: now };
        }
      });
      if (Object.keys(updates).length === 0) {
        toast('All pages already have SEO data. No changes made.', { icon: 'ℹ️' });
        return;
      }
      await update(ref(database, 'seo_metadata'), updates);
      setSeoData(prev => ({ ...prev, ...updates }));
      toast.success(`Seeded ${Object.keys(updates).length} pages with default SEO data`);
    } catch (error) {
      console.error('Error seeding defaults:', error);
      toast.error('Seeding failed: ' + error.message);
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-gray-800 text-white p-6 flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FontAwesomeIcon icon={faGlobe} />
            SEO Management
          </h2>
          <p className="text-gray-400 mt-1">Manage meta tags for search engine optimization</p>
        </div>
        {/* <button
          onClick={handleSeedDefaults}
          disabled={seeding}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm px-4 py-2 rounded transition-colors"
        >
          <FontAwesomeIcon icon={faMagic} />
          {seeding ? 'Seeding…' : 'Seed Defaults'}
        </button> */}
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className="w-full md:w-1/4 bg-gray-50 border-r border-gray-200 overflow-y-auto" style={{ maxHeight: '600px' }}>
          {PAGES.map(page => (
            <button
              key={page.id}
              onClick={() => setActiveTab(page.id)}
              className={`w-full text-left px-6 py-4 border-b border-gray-100 transition-colors ${
                activeTab === page.id
                  ? 'bg-white text-blue-600 border-l-4 border-l-blue-600 font-semibold'
                  : 'hover:bg-gray-100'
              }`}
            >
              {page.name}
              <div className="text-xs text-gray-400 font-normal">{page.path}</div>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="w-full md:w-3/4 p-8">
          {PAGES.map(page => (
            <div key={page.id} className={activeTab === page.id ? 'block' : 'hidden'}>
              <div className="mb-6 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Settings for: <span className="text-blue-600">{page.name}</span>
                </h3>
                <button
                  onClick={() => handleSave(page.id)}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors"
                >
                  <FontAwesomeIcon icon={faSave} />
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                  <input
                    type="text"
                    value={seoData[page.id]?.title || ''}
                    onChange={e => handleInputChange(page.id, 'title', e.target.value)}
                    placeholder="Enter meta title (recommended 50-60 characters)"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <div className="mt-1 text-xs text-gray-500 flex justify-between">
                    <span>Characters: {(seoData[page.id]?.title || '').length}</span>
                    <span>Target: 50-60</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                  <textarea
                    rows="4"
                    value={seoData[page.id]?.description || ''}
                    onChange={e => handleInputChange(page.id, 'description', e.target.value)}
                    placeholder="Enter meta description (recommended 150-160 characters)"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <div className="mt-1 text-xs text-gray-500 flex justify-between">
                    <span>Characters: {(seoData[page.id]?.description || '').length}</span>
                    <span>Target: 150-160</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                  <input
                    type="text"
                    value={seoData[page.id]?.keywords || ''}
                    onChange={e => handleInputChange(page.id, 'keywords', e.target.value)}
                    placeholder="e.g. luxury villas, dharampur, staycation (comma separated)"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-500 italic">Separate keywords with commas.</p>
                </div>

                {seoData[page.id]?.updatedAt && (
                  <p className="text-xs text-gray-400 pt-4">
                    Last updated: {new Date(seoData[page.id].updatedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeoManager;
